import React, { createContext, useEffect, useState } from 'react';

export const SettingsContext = createContext();

export const APP_THEME_STORAGE_KEY = 'appTheme';
export const MANUAL_SKIP_CONTROLS_STORAGE_KEY = 'manualSkipControlsEnabled';
export const MANUAL_EXTENSION_CONTROLS_STORAGE_KEY = 'manualExtensionControlsEnabled';
export const SIGNAL_VOLUME_STORAGE_KEY = 'signalVolume';
export const SHOW_SHORTCUT_HINTS_STORAGE_KEY = 'showShortcutHints';
export const DEFAULT_APP_THEME_ID = 'aurora';
export const DEFAULT_SIGNAL_VOLUME = 0.7;

export const APP_THEMES = [
  {
    id: 'aurora',
    name: 'Aurora',
    description: 'Cool sky blues with calm mint accents',
    swatches: ['#7abed4', '#2aa52e', '#d8ecf3'],
  },
  {
    id: 'ember',
    name: 'Ember',
    description: 'Warm sandstone and ember reds for a richer desk mood',
    swatches: ['#d56a43', '#b8902e', '#f1dfd0'],
  },
  {
    id: 'midnight',
    name: 'Midnight',
    description: 'Dark slate tones with luminous cyan highlights',
    swatches: ['#69c5ff', '#4ed7cf', '#23384b'],
  },
  {
    id: 'sage',
    name: 'Sage',
    description: 'Muted eucalyptus greens with clean stone neutrals',
    swatches: ['#5a8f7b', '#89b67b', '#edf3ee'],
  },
  {
    id: 'dusk',
    name: 'Dusk',
    description: 'Soft violet dusk tones with brighter orchid accents',
    swatches: ['#7c6dd0', '#c45ccf', '#f3eefc'],
  },
  {
    id: 'graphite',
    name: 'Graphite',
    description: 'Refined charcoal surfaces with crisp electric blue cues',
    swatches: ['#4d6580', '#7bc6ff', '#1f2933'],
  },
];

export function getThemeMeta(themeId) {
  return APP_THEMES.find((theme) => theme.id === themeId) || APP_THEMES[0];
}

export const SettingsProvider = ({ children }) => {
  const [showSettings, setShowSettings] = useState(false);
  const [activeThemeId, setActiveThemeId] = useState(() => (
    localStorage.getItem(APP_THEME_STORAGE_KEY) || DEFAULT_APP_THEME_ID
  ));
  const [manualSkipControlsEnabled, setManualSkipControlsEnabled] = useState(() => (
    localStorage.getItem(MANUAL_SKIP_CONTROLS_STORAGE_KEY) === 'true'
  ));
  const [manualExtensionControlsEnabled, setManualExtensionControlsEnabled] = useState(() => (
    localStorage.getItem(MANUAL_EXTENSION_CONTROLS_STORAGE_KEY) === 'true'
  ));
  const [showShortcutHints, setShowShortcutHints] = useState(() => (
    localStorage.getItem(SHOW_SHORTCUT_HINTS_STORAGE_KEY) !== 'false'
  ));
  const [signalVolume, setSignalVolume] = useState(() => {
    const storedValue = Number.parseFloat(localStorage.getItem(SIGNAL_VOLUME_STORAGE_KEY));
    return Number.isFinite(storedValue) ? storedValue : DEFAULT_SIGNAL_VOLUME;
  });

  useEffect(() => {
    localStorage.setItem(APP_THEME_STORAGE_KEY, activeThemeId);

    const themeClasses = APP_THEMES.map((theme) => `theme-palette-${theme.id}`);
    document.body.classList.remove(...themeClasses);
    document.body.classList.add(`theme-palette-${activeThemeId}`);

    return () => {
      document.body.classList.remove(...themeClasses);
    };
  }, [activeThemeId]);

  useEffect(() => {
    localStorage.setItem(SIGNAL_VOLUME_STORAGE_KEY, String(signalVolume));
  }, [signalVolume]);

  useEffect(() => {
    localStorage.setItem(SHOW_SHORTCUT_HINTS_STORAGE_KEY, String(showShortcutHints));
  }, [showShortcutHints]);

  return (
    <SettingsContext.Provider
      value={{
        showSettings,
        setShowSettings,
        activeThemeId,
        setActiveThemeId,
        manualSkipControlsEnabled,
        setManualSkipControlsEnabled,
        manualExtensionControlsEnabled,
        setManualExtensionControlsEnabled,
        signalVolume,
        setSignalVolume,
        showShortcutHints,
        setShowShortcutHints,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};
